import { useRef, useState } from 'react';
import { api } from '../../api';
import type { Tarif } from '../../billtoro/types';
import { fehlendeFunktion, planName, upgradeAnbieten } from '../../billtoro/upgrade';
import { ConfirmDialog } from '../../components/Dialog';
import { fmtDate, fmtKB } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { PdfDialog } from '../PdfDialog';
import { fehlerText, GesperrtKnopf } from '../tarifHinweis';
import { DATEIEN_MAX, dateienUrl, dateiUrl, type KontaktArt, type KontaktDatei } from './KontaktDaten';
import './kontakte.css';

const ART: Record<string, string> = {
  'application/pdf': 'PDF',
  'image/jpeg': 'JPEG',
  'image/png': 'PNG',
  'image/gif': 'GIF',
  'image/webp': 'WebP',
  'image/heic': 'HEIC',
};
// Fotos, die jeder Browser zeigt (HEIC nur zum Herunterladen)
const BILD = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp']);

// Karte „Dateien“ im Kunden- und Lieferanten-Detail (nur Billtoro): PDF und Fotos ablegen, ansehen, herunterladen und
// löschen. Hochladen und Zuordnen in einer Anfrage; der Server prüft Tarif (ab Start), Grenzen und Virenscan. Ohne die
// Funktion im Tarif: Knopf mit Marke „Start“, vorhandene Dateien bleiben. Kein Name dessen, der eine Datei abgelegt hat.
// Stundenzettel aus dem Stapel darunter, je Objekt ein zugeklappter Ordner, jeder Zettel mit der Rechnung dazu (bzw.
// „Entwurf“); sie zählen nicht zu den Dateien je Kontakt.
export function Dateien({
  art,
  kontaktId,
  dateien,
  maxMb,
  tarif,
  pflegen,
  onChanged,
}: {
  art: KontaktArt;
  kontaktId: string;
  dateien: KontaktDatei[];
  // höchstens so groß je Datei (vom Server)
  maxMb: number;
  tarif: Tarif | null | undefined;
  pflegen: boolean;
  onChanged(): Promise<void>;
}) {
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [pdf, setPdf] = useState<KontaktDatei | null>(null);
  const [weg, setWeg] = useState<KontaktDatei | null>(null);
  const fehlt = fehlendeFunktion(tarif, 'kontaktdateien');
  const eigene = dateien.filter((d) => !d.stundenzettel);
  // Ordner der Stundenzettel, der mit dem neuesten Zettel zuerst (die Liste kommt neueste zuerst)
  const ordner = new Map<string, KontaktDatei[]>();
  for (const d of dateien) if (d.stundenzettel) ordner.set(d.stundenzettel.ordner, [...(ordner.get(d.stundenzettel.ordner) ?? []), d]);
  const voll = eigene.length >= DATEIEN_MAX;

  async function hochladen(f: File | undefined) {
    if (input.current) input.current.value = '';
    if (!f) return;
    setFehler(null);
    if (f.size > maxMb * 1024 * 1024) return setFehler(`Die Datei ist zu groß (höchstens ${maxMb} MB).`);
    const fd = new FormData();
    fd.append('file', f, f.name);
    setBusy(true);
    try {
      await api.post(dateienUrl(art, kontaktId), fd);
      await onChanged();
      toast('Datei gespeichert');
    } catch (err) {
      setFehler(fehlerText(err));
    } finally {
      setBusy(false);
    }
  }

  async function loeschen(d: KontaktDatei) {
    setBusy(true);
    try {
      await api.del(dateiUrl(d.id));
      await onChanged();
      toast(`Datei „${d.name}“ gelöscht`);
    } catch (err) {
      const t = fehlerText(err);
      if (t) toast(t);
    } finally {
      setBusy(false);
      setWeg(null);
    }
  }

  function zeile(d: KontaktDatei) {
    const z = d.stundenzettel;
    return (
      <li key={d.id} className="kt-datei">
        <div className="kt-datei-text">
          {d.mime === 'application/pdf' ? (
            <button type="button" className="linkbtn kt-datei-name" onClick={() => setPdf(d)}>
              {d.name}
            </button>
          ) : BILD.has(d.mime) ? (
            <a className="kt-datei-name" href={`${dateiUrl(d.id)}?inline=1`} target="_blank" rel="noopener">
              {d.name}
            </a>
          ) : (
            <span className="kt-datei-name">{d.name}</span>
          )}
          {/* Rechnung bzw. „Entwurf“ vorn in der Zeile darunter: steht bei langen und kurzen Namen an derselben Stelle */}
          <span className="muted kt-meta">
            {z && (z.rechnung || z.entwurf) && <span className="kt-wort kt-beleg">{z.rechnung ?? 'Entwurf'}</span>}
            {ART[d.mime] ?? 'Datei'} · {fmtKB(d.groesse)} · {fmtDate(d.am)}
          </span>
        </div>
        <div className="kt-zeile-act">
          <a className="linkbtn" href={dateiUrl(d.id)} download>
            Herunterladen
          </a>
          {pflegen && (
            <button type="button" className="linkbtn rt-danger" onClick={() => setWeg(d)}>
              Löschen
            </button>
          )}
        </div>
      </li>
    );
  }

  return (
    <section className="rt-card kt-karte" aria-labelledby={`kt-dateien-${art}`}>
      <div className="kt-karte-kopf">
        <h2 id={`kt-dateien-${art}`}>Dateien</h2>
        {pflegen &&
          !voll &&
          (fehlt ? (
            <GesperrtKnopf tarif={tarif} fehlt={fehlt} className="btn small" onUpgrade={() => upgradeAnbieten(tarif, 'kontaktdateien')}>
              + Datei hinzufügen
            </GesperrtKnopf>
          ) : (
            <button type="button" className="btn small" disabled={busy} onClick={() => input.current?.click()}>
              {busy ? 'Wird hochgeladen …' : '+ Datei hinzufügen'}
            </button>
          ))}
        {/* ohne capture: am Handy bietet das Gerät Kamera, Fotos und Dateien an */}
        <input
          ref={input}
          type="file"
          accept="application/pdf,image/*"
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
          onChange={(e) => void hochladen(e.target.files?.[0])}
        />
      </div>
      {dateien.length === 0 ? (
        <p className="muted kt-leer">Verträge, Fotos oder andere PDFs zu diesem {art === 'kunde' ? 'Kunden' : 'Lieferanten'} ablegen.</p>
      ) : (
        eigene.length > 0 && <ul className="kt-liste">{eigene.map(zeile)}</ul>
      )}
      {[...ordner].map(([name, list]) => (
        <details key={name} className="kt-ordner">
          <summary>
            <span>
              Stundenzettel · {name} <span className="muted">({list.length})</span>
            </span>
          </summary>
          <ul className="kt-liste">{list.map(zeile)}</ul>
        </details>
      ))}
      {fehler && <div className="notice bad">{fehler}</div>}
      {fehlt && fehlt !== 'bald' && dateien.length > 0 && tarif ? (
        <p className="rt-hint kt-klein">Neue Dateien gibt es ab dem Tarif {planName(tarif, fehlt)}. Ihre Dateien bleiben.</p>
      ) : (
        pflegen && (
          <p className="rt-hint kt-klein">
            PDF oder Foto, höchstens {maxMb} MB. Höchstens {DATEIEN_MAX} Dateien je Kontakt. Jede Datei wird auf Viren geprüft.
          </p>
        )
      )}

      <PdfDialog
        url={pdf ? `${dateiUrl(pdf.id)}?inline=1` : null}
        title={pdf?.name ?? ''}
        download={pdf ? dateiUrl(pdf.id) : undefined}
        onClose={() => setPdf(null)}
      />
      <ConfirmDialog
        open={!!weg}
        title={weg ? `Datei „${weg.name}“ löschen?` : ''}
        text={
          weg?.stundenzettel?.rechnung
            ? `Sie ist danach endgültig weg, auch bei der Rechnung ${weg.stundenzettel.rechnung}.`
            : 'Sie ist danach endgültig weg.'
        }
        confirmLabel="Ja, löschen"
        danger
        busy={busy}
        onConfirm={() => weg && void loeschen(weg)}
        onCancel={() => setWeg(null)}
      />
    </section>
  );
}
